/**
 * TICK-3555: Cart quantity-break (volume) pricing.
 *
 * The Horizon cart only renders Shopify's regular line price. TPI's bulk pricing
 * lives in the custom paper-clip-app, exposed at the discounts endpoint (the same
 * one the quote builder uses). This script posts the current cart lines to that
 * endpoint and, for any line that qualifies, strikes through the regular line
 * price and shows the discounted quantity price beside it.
 *
 * Endpoint contract:
 *   POST { items: [{ product_id, variant_id, price(cents), quantity }] }
 *   ->   { total, discounts: { [variant_id]: perUnitDiscountInCents } }
 *
 * The discount is informational on the cart (it is actually applied when a quote /
 * draft order is created); this just surfaces it so customers see the bulk price.
 */
(function () {
  const ENDPOINT =
    'https://us-central1-paper-clip-app.cloudfunctions.net/apiv2/api-v2/discounts/get-discounts';
  const SELECTOR = '.tpi-volume-price';

  // ---- money formatting (Shopify-compatible, driven by shop.money_format) ----
  const MONEY_FORMAT = window.tpiMoneyFormat || '${{amount}}';

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(cents) || cents == null) return '0';
    const value = (cents / 100.0).toFixed(precision);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  function formatMoney(cents, formatOverride) {
    const fmt = formatOverride || MONEY_FORMAT;
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount';
    let value = '';
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  // ---------------------------------------------------------------------------
  let observer = null;
  let debounceTimer = null;

  function collectItems(wraps) {
    return wraps.map((w) => ({
      product_id: parseInt(w.dataset.productId, 10) || 0,
      variant_id: w.dataset.variantId,
      price: parseInt(w.dataset.unitPrice, 10) || 0,
      quantity: parseInt(w.dataset.quantity, 10) || 1,
    }));
  }

  function applyTreatment(regular, discounted, fullAmount, discountedAmount, fmt) {
    if (!regular || !discounted) return;
    if (discountedAmount >= 0 && discountedAmount < fullAmount) {
      regular.classList.add('tpi-volume-price__regular--struck');
      discounted.textContent = formatMoney(discountedAmount, fmt);
      discounted.hidden = false;
    } else {
      regular.classList.remove('tpi-volume-price__regular--struck');
      discounted.textContent = '';
      discounted.hidden = true;
    }
  }

  function render(wraps, data) {
    const discounts = (data && data.discounts) || {};
    const totalDiscount = parseInt(data && data.total, 10) || 0;

    // Pause observing while we mutate the DOM so we don't re-trigger ourselves.
    if (observer) observer.disconnect();

    // Per-line prices
    for (const w of wraps) {
      const vid = w.dataset.variantId;
      const qty = parseInt(w.dataset.quantity, 10) || 1;
      const linePrice = parseInt(w.dataset.linePrice, 10) || 0;
      const perUnit = parseInt(discounts[vid], 10) || 0;
      applyTreatment(
        w.querySelector('.tpi-volume-price__regular'),
        w.querySelector('.tpi-volume-price__discounted'),
        linePrice,
        perUnit > 0 ? linePrice - perUnit * qty : linePrice,
        w.dataset.moneyFormat
      );
    }

    // Cart total — endpoint `total` is the sum of all line discounts in cents.
    const totalWrap = document.querySelector('.tpi-volume-total');
    if (totalWrap) {
      const cartTotal = parseInt(totalWrap.dataset.cartTotal, 10) || 0;
      applyTreatment(
        totalWrap.querySelector('.tpi-volume-total__regular'),
        totalWrap.querySelector('.tpi-volume-total__discounted'),
        cartTotal,
        totalDiscount > 0 ? cartTotal - totalDiscount : cartTotal,
        totalWrap.dataset.moneyFormat
      );
    }

    reconnect();
  }

  function setLoading(on) {
    // Toggles the hidden attribute only (an attribute change, which the
    // childList observer ignores — so this never re-triggers apply()).
    const el = document.querySelector('.tpi-volume-loading');
    if (el) el.hidden = !on;
  }

  async function apply() {
    const wraps = Array.from(document.querySelectorAll(SELECTOR));
    if (!wraps.length) return;

    setLoading(true);
    let data;
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: collectItems(wraps) }),
      });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      data = await res.json();
    } catch (err) {
      setLoading(false);
      return; // Network/endpoint error — leave regular prices as-is.
    }

    render(wraps, data || {});
    setLoading(false);
  }

  function scheduleApply() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(apply, 150);
  }

  function reconnect() {
    // Observe a container that includes BOTH the line items and the summary total,
    // since Horizon re-renders the whole cart section on quantity change. Debounced,
    // and we disconnect during our own DOM writes, so the broad scope is safe.
    const target =
      document.querySelector('cart-items-component')?.closest('.shopify-section') ||
      document.querySelector('cart-items-component')?.parentElement ||
      document.body;
    if (target) {
      observer.observe(target, { childList: true, subtree: true });
    }
  }

  function init() {
    observer = new MutationObserver(scheduleApply);
    reconnect();
    scheduleApply();
    // Horizon dispatches cart updates; re-apply on those too (belt and braces).
    document.addEventListener('cart:update', scheduleApply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
